/* eslint-disable no-param-reassign, no-console, no-plusplus, no-continue, @typescript-eslint/no-empty-function, @typescript-eslint/consistent-type-imports */

import type {
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  Event,
} from '@sentry/browser';
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/types';

const sentryBrowserPromise: Promise<
  Pick<
    typeof import('@sentry/browser'),
    | 'BrowserClient'
    | 'defaultStackParser'
    | 'captureException'
    | 'captureMessage'
    | 'getCurrentHub'
    | 'GlobalHandlers'
    | 'InboundFilters'
    | 'FunctionToString'
    | 'TryCatch'
    | 'Breadcrumbs'
    | 'LinkedErrors'
    | 'HttpContext'
    | 'Dedupe'
    | 'makeFetchTransport'
    | 'configureScope'
  >
> = import(
  /* webpackChunkName: "sentry" */
  /* webpackExports: ["BrowserClient", "defaultStackParser", "captureException", "captureMessage", "getCurrentHub", "GlobalHandlers", "InboundFilters", "FunctionToString", "TryCatch", "Breadcrumbs", "LinkedErrors", "HttpContext", "Dedupe", "makeFetchTransport", "configureScope"] */
  '@sentry/browser'
);

const sentryTracingPromise: Promise<
  Pick<typeof import('@sentry/tracing'), 'BrowserTracing'>
> = import(
  /* webpackChunkName: "sentry-tracing" */
  /* webpackExports: ["BrowserTracing"] */
  '@sentry/tracing'
);

const sentryIntegrationsPromise: Promise<
  Pick<typeof import('@sentry/integrations'), 'RewriteFrames'>
> = import(
  /* webpackChunkName: "sentry-integrations" */
  /* webpackExports: ["RewriteFrames"] */
  '@sentry/integrations'
);

function createRejectedSentryPromise() {
  return new Promise<never>(() => {});
}

export const sentryPromise =
  typeof window !== 'undefined' && window.SENTRY
    ? Promise.all([
        sentryBrowserPromise,
        sentryTracingPromise,
        sentryIntegrationsPromise,
      ]).then(([sentryBrowser, sentryTracing, sentryIntegrations]) => ({
        ...sentryBrowser,
        ...sentryTracing,
        ...sentryIntegrations,
      }))
    : createRejectedSentryPromise();

const typeErrorFetchFailedValues = new Set([
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'Load failed',
]);

function shouldIgnoreError(error: ErrorEvent, hint: EventHint): boolean {
  return (
    isRecaptchaBadTimeoutRejection(error, hint) ||
    isNotFoundError(error) ||
    isSchibstedNetworkError(error)
  );
}

// https://github.com/getsentry/sentry-javascript/issues/2514
function isRecaptchaBadTimeoutRejection(
  _: ErrorEvent,
  hint: EventHint
): boolean {
  return hint.originalException === 'Timeout';
}

function isSchibstedNetworkError(error: ErrorEvent): boolean {
  const exception = error.exception?.values?.[0];
  const now = Date.now();

  // Schibsted ads/tracking SDK is not try-catching their stuff properly so it ends up here
  // Since not all errors have stack traces attached to them (see https://github.com/getsentry/sentry-javascript/issues/7275#issuecomment-1443091905),
  // we have to filter by breadcrumbs. Since we cannot assume that the error is the last breadcrumb (I remember seeing an instance of this in Sentry, however, can't find the link any more), we need to check all of them.
  if (
    exception?.type !== 'TypeError' ||
    !typeErrorFetchFailedValues.has(exception.value as string)
  ) {
    return false;
  }

  if (!error.breadcrumbs) {
    return false;
  }

  // We go from the back since the last breadcrumb is most likely the erroneous one
  for (let i = error.breadcrumbs.length - 1; i >= 0; i--) {
    const breadcrumb = error.breadcrumbs[i];
    if (!breadcrumb) continue;

    // We only need to check the last 5s of breadcrumbs as any earlier breadcrumbs are definitely unrelated
    if (breadcrumb.timestamp && now - breadcrumb.timestamp * 1000 > 5000) {
      break;
    }

    if (isErroneousSchibstedBreadcrumb(breadcrumb)) {
      return true;
    }
  }

  return false;
}

function isErroneousSchibstedBreadcrumb(breadcrumb: Breadcrumb): boolean {
  if (
    breadcrumb.level !== 'error' ||
    (breadcrumb.category !== 'xhr' && breadcrumb.category !== 'fetch')
  ) {
    return false;
  }

  const url = breadcrumb.data?.url as string | undefined;
  if (!url) return false;

  return (
    url === 'https://secure.adnxs.com/getuidj' ||
    // cis.schibsted.com, cis.prisjakt.nu, cis.prisjagt.dk etc.
    url.startsWith('https://cis.') ||
    // https://collector.schibsted.io/api/v1/track/sdrn:schibsted:client:prisjaktse, ...:prisjaktno, ...:prisjagtdk etc.
    url.startsWith('https://collector.schibsted.io')
  );
}

function isNotFoundError(error: ErrorEvent): boolean {
  return (error as any).status === 404 || (error as any).status === 410;
}

export function initSentry() {
  sentryPromise.then(
    ({
      BrowserClient,
      defaultStackParser,
      getCurrentHub,
      GlobalHandlers,
      InboundFilters,
      FunctionToString,
      TryCatch,
      Breadcrumbs,
      LinkedErrors,
      HttpContext,
      Dedupe,
      makeFetchTransport,
      BrowserTracing,
      RewriteFrames,
    }) => {
      // See https://docs.sentry.io/platforms/javascript/configuration/tree-shaking/ for what is happening
      const client = new BrowserClient({
        // debug is defined in webpack.config.js with DefinePlugin
        dsn: window.SENTRY,
        release: window.RELEASE,
        environment: window.ENVIRONMENT,
        tracesSampleRate: 0.0001,
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        beforeSend: (event, hint) => {
          return shouldIgnoreError(event, hint) ? null : addEventTags(event);
        },
        beforeSendTransaction: addEventTags,
        normalizeDepth: 6,
        ignoreErrors: [
          'auth.invalid-user-credentials',
          'Blocked a frame with origin "https://www.prisjakt.nu" from accessing a cross-origin frame',
        ],
        integrations: [
          new BrowserTracing(),
          new GlobalHandlers(),
          new InboundFilters(),
          new FunctionToString(),
          new TryCatch(),
          new Breadcrumbs(),
          new LinkedErrors(),
          new HttpContext(),
          new Dedupe(),
          new RewriteFrames({
            root: 'webpack://@prisjakt/responsive-frontend/./',
            prefix: 'rfe-browser:///',
          }),
        ],
        allowUrls: [
          /^https:\/\/pjrfe-75b8\.kxcdn\.com/,
          /^https:\/\/www\.prisjakt\.(nu|no)/,
          /^https:\/\/pricespy\.co\.(uk|nz)/,
          /^https:\/\/prisjagt\.dk/,
          /^https:\/\/hintaopas\.fi/,
          /^https:\/\/ledenicheur\.fr/,
          /^https?:\/\/local\.prisjakt\.nu/,
          /^https?:\/\/localhost/,
        ],
      });

      getCurrentHub().bindClient(client);
    }
  );
}

function addEventTags<E extends Event>(event: E): E {
  event.tags = event.tags ?? {};

  event.tags.market = window.CLIENT_MARKET.code;

  return event;
}

// Used by sagas
export function captureException(
  ...args: Parameters<typeof sentryCaptureException>
) {
  sentryPromise
    .then(({ captureException: sentryCaptureException }) =>
      sentryCaptureException(...args)
    )
    .catch(() => {
      console.error(...args);
    });
}

// Used by sagas
export function captureMessage(
  ...args: Parameters<typeof sentryCaptureMessage>
) {
  sentryPromise
    .then(({ captureMessage: sentryCaptureMessage }) =>
      sentryCaptureMessage(...args)
    )
    .catch(() => {
      console.log(...args);
    });
}
